import { Injectable } from "@angular/core";
import { from, map, Observable } from "rxjs";
import * as ExcelJS from "exceljs";

@Injectable({
  providedIn: "root",
})
export class OnHandReportService {
  private defaultFontColor: string = "FF000000";

  private headers: Record<string, CellColor> = {
    "Product Name": { bgColor: "fffafabb" },
    SKU: { bgColor: "fffafabb" },
    "Supplier(Vendor)": { bgColor: "fffafabb" },
    "Verified RFID": { bgColor: "0097f4" },
    "Fulfilled Orders": { bgColor: "ff96bf48" },
    "Projected On Hand": { bgColor: "ff0097f4" },
    "Shopify On Hand": { bgColor: "ff96bf48" },
    Discrepancy: { bgColor: "fffafabb" },
  };

  constructor() {}

  public generateReport(items: Array<any>): Observable<Blob> {
    const workbook: ExcelJS.Workbook = new ExcelJS.Workbook();
    const worksheet: ExcelJS.Worksheet =
      workbook.addWorksheet("Compare On Hand");

    var rowIndex = 1;
    const row: ExcelJS.Row = worksheet.getRow(rowIndex);

    this.insertHeaders(row);
    rowIndex++;
    items.forEach((item) => {
      const row: ExcelJS.Row = worksheet.getRow(rowIndex);
      var color = OnHandCellColor[item.FilterType];
      const discrepancy = item.NewCount - item.CurrentCount;
      const formattedDiscrepancy =
        discrepancy > 0 ? `+${discrepancy}` : discrepancy.toString();
      const values = [
        item.Title,
        item.Sku,
        item.SupplierName,
        item.RfidCount,
        item.OrdersCount,
        item.NewCount,
        item.CurrentCount,
        formattedDiscrepancy,
      ];

      values.forEach((value, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = value;
        this.setColorAndBorder(cell, color);
        cell.numFmt = "@";
      });
      rowIndex++;
    });

    this.createOuterBorder(
      worksheet,
      { row: 1, col: 1 },
      { row: items.length + 1, col: Object.keys(this.headers).length }
    );
    this.autoFit(worksheet);

    return from(workbook.xlsx.writeBuffer()).pipe(
      map((_) => {
        return new Blob([_], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      })
    );
  }

  private insertHeaders(row) {
    const keys = Object.keys(this.headers);

    keys.forEach((key, index) => {
      const cellIndex = index + 1;
      const cellColor = this.headers[key];
      const richText: ExcelJS.RichText[] = [
        {
          font: {
            bold: true,
            color: { argb: cellColor.fontColor ?? this.defaultFontColor },
          },
          text: key,
        },
      ];

      const cell = row.getCell(cellIndex);
      cell.value = { richText };

      this.setColorAndBorder(cell, cellColor.bgColor);
    });
  }
  private setColorAndBorder(cell: ExcelJS.Cell, color: string) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: color },
    };

    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }

  private autoFit(worksheet: ExcelJS.Worksheet) {
    worksheet.columns?.forEach((column, i) => {
      if (!column) return;

      let maxLength = 10;

      column.eachCell?.({ includeEmpty: true }, (cell) => {
        let columnLength = 10;

        if (cell.value != null) {
          if (typeof cell.value === "object") {
            if ("richText" in cell.value) {
              // richText → concatenate text
              columnLength = cell.value.richText
                .map((t: any) => t.text)
                .join("").length;
            } else if ("text" in cell.value) {
              columnLength = cell.value.text.length;
            } else {
              columnLength = JSON.stringify(cell.value).length;
            }
          } else {
            columnLength = cell.value.toString().length;
          }
        }

        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });

      column.width = Math.max(maxLength + 2, 10); // add padding
    });
  }

  private createOuterBorder(
    worksheet: ExcelJS.Worksheet,
    start = { row: 1, col: 1 },
    end = { row: 1, col: 1 },
    borderWidth: ExcelJS.BorderStyle = "medium"
  ) {
    const borderStyle: Partial<ExcelJS.Border> = {
      style: borderWidth,
    };
    for (let i = start.row; i <= end.row; i++) {
      const leftBorderCell = worksheet.getCell(i, start.col);
      const rightBorderCell = worksheet.getCell(i, end.col);
      leftBorderCell.border = {
        ...leftBorderCell.border,
        left: borderStyle,
      };
      rightBorderCell.border = {
        ...rightBorderCell.border,
        right: borderStyle,
      };
    }

    for (let i = start.col; i <= end.col; i++) {
      const topBorderCell = worksheet.getCell(start.row, i);
      const bottomBorderCell = worksheet.getCell(end.row, i);
      topBorderCell.border = {
        ...topBorderCell.border,
        top: borderStyle,
      };
      bottomBorderCell.border = {
        ...bottomBorderCell.border,
        bottom: borderStyle,
      };
    }
  }
}

enum OnHandCellColor {
  Deflated = "ffb5f4b5",
  Inflated = "fff8c294",
  OutOfStock = "fffb8b8b",
  Default = "FFFFFFFF",
}

interface CellColor {
  bgColor: string;
  fontColor?: string;
}
